import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import { Avatar } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash, Camera } from "@phosphor-icons/react";

const field = (label, k, type = "text") => ({ label, k, type });

const BASIC = [
  field("Date of Birth", "date_of_birth", "date"),
  field("Gender", "gender"),
  field("Marital Status", "marital_status"),
  field("Nationality", "nationality"),
];
const CONTACT = [
  field("Personal Email", "personal_email", "email"),
  field("Mobile Number", "mobile_number"),
  field("Alternate Phone", "alternate_phone"),
  field("City", "city"),
  field("State", "state"),
  field("Country", "country"),
  field("Postal Code", "postal_code"),
];
const EMP = [
  field("Employment Type", "employment_type"),
  field("Work Location", "work_location"),
  field("Office Location", "office_location"),
  field("Probation End Date", "probation_end_date", "date"),
  field("Confirmation Date", "confirmation_date", "date"),
];

export default function MyProfile() {
  const [data, setData] = useState(null);
  const [p, setP] = useState({});
  const [bank, setBank] = useState({});
  const [stat, setStat] = useState({});
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newEmer, setNewEmer] = useState({ name: "", relationship: "", phone: "", email: "" });

  const load = async () => {
    try { const r = await api.get("/profile/me"); setData(r.data); setP(r.data.personal || {}); setBank(r.data.bank || {}); setStat(r.data.statutory || {}); setPhoto((r.data.personal || {}).profile_photo || null); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-neutral-500">Loading…</div>;

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/me/photo`, {
        method: "POST", credentials: "include", body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      setPhoto(d.profile_photo);
      toast.success("Photo updated");
    } catch (err) { toast.error(err.message); }
    e.target.value = ""; setUploading(false);
  };

  const savePersonal = async () => { await api.put("/profile/me", p); toast.success("Saved"); load(); };
  const saveBank = async () => { await api.put("/profile/me/bank", bank); toast.success("Bank saved"); load(); };
  const addEmer = async () => {
    if (!newEmer.name) return;
    await api.post("/profile/emergency-contacts", newEmer);
    setNewEmer({ name: "", relationship: "", phone: "", email: "" });
    load();
  };
  const delEmer = async (id) => { await api.delete(`/profile/emergency-contacts/${id}`); load(); };

  const current = (list) => (
    <div className="grid grid-cols-2 gap-3">
      {list.map((f) => (
        <div key={f.k}><div className="eyebrow mb-1">{f.label}</div>
          <Input type={f.type} value={p[f.k] || ""} onChange={(e) => setP({ ...p, [f.k]: e.target.value })} className="rounded-none" data-testid={`pf-${f.k}`} />
        </div>
      ))}
    </div>
  );

  const maskAccount = (acc) => !acc ? "" : acc.length < 4 ? acc : `XXXX XXXX ${acc.slice(-4)}`;

  return (
    <div data-testid="my-profile">
      <PageHeader overline="Account" title="My Profile" subtitle="Keep your personal details, emergency contact and bank information up to date." />

      <div className="bg-white border border-[#E5E5E5] p-6 mb-4 flex items-center gap-6">
        <div className="relative">
          <Avatar name={data.user?.name} photo={photo} size={80} />
          <label className="absolute -bottom-2 -right-2 h-8 w-8 bg-[#FF5500] text-white grid place-items-center cursor-pointer hover:bg-[#E64D00]" title="Upload photo" data-testid="upload-my-photo">
            <Camera size={15} />
            <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" disabled={uploading} />
          </label>
        </div>
        <div>
          <div className="eyebrow">{data.user?.employee_id || "—"}</div>
          <div className="font-head text-3xl font-bold tracking-tight">{data.user?.name}</div>
          <div className="text-sm text-neutral-500">{data.user?.designation} · {data.user?.department}</div>
          <div className="text-xs text-neutral-500 mt-1">{data.user?.email} · Joined {data.user?.joining_date || "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Personal Information" right={<Button size="sm" onClick={savePersonal} className="rounded-none bg-[#FF5500] h-8 text-xs" data-testid="save-personal">Save</Button>}>
          {current(BASIC)}
        </Section>
        <Section title="Contact & Address" right={<Button size="sm" onClick={savePersonal} className="rounded-none bg-[#FF5500] h-8 text-xs">Save</Button>}>
          {current(CONTACT)}
          <div className="mt-3">
            <div className="eyebrow mb-1">Current Address</div>
            <Textarea value={p.current_address || ""} onChange={(e) => setP({ ...p, current_address: e.target.value })} className="rounded-none" rows={2} />
          </div>
          <div className="mt-3">
            <div className="eyebrow mb-1">Permanent Address</div>
            <Textarea value={p.permanent_address || ""} onChange={(e) => setP({ ...p, permanent_address: e.target.value })} className="rounded-none" rows={2} />
          </div>
        </Section>
        <Section title="Employment Information" right={<Button size="sm" onClick={savePersonal} className="rounded-none bg-[#FF5500] h-8 text-xs">Save</Button>}>
          {current(EMP)}
          <div className="mt-3 text-sm" data-testid="my-reporting-admin">
            <span className="eyebrow mr-2">Reporting Admin</span>{data.user?.reporting_admin_name || "—"}
          </div>
        </Section>
        <Section title="Bank / Payment" right={<Button size="sm" onClick={saveBank} className="rounded-none bg-[#FF5500] h-8 text-xs" data-testid="save-bank">Save</Button>}>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="eyebrow mb-1">Bank Name</div><Input value={bank.bank_name || ""} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className="rounded-none" /></div>
            <div><div className="eyebrow mb-1">Account Holder</div><Input value={bank.account_holder || ""} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} className="rounded-none" /></div>
            <div><div className="eyebrow mb-1">Account Number</div><Input value={bank.account_number || ""} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} className="rounded-none" data-testid="bank-acc" /></div>
            <div><div className="eyebrow mb-1">IFSC Code</div><Input value={bank.ifsc_code || ""} onChange={(e) => setBank({ ...bank, ifsc_code: e.target.value })} className="rounded-none" /></div>
            <div><div className="eyebrow mb-1">Branch</div><Input value={bank.branch || ""} onChange={(e) => setBank({ ...bank, branch: e.target.value })} className="rounded-none" /></div>
            <div><div className="eyebrow mb-1">UPI ID</div><Input value={bank.upi_id || ""} onChange={(e) => setBank({ ...bank, upi_id: e.target.value })} className="rounded-none" /></div>
          </div>
          {bank.account_number && <div className="text-xs text-neutral-500 mt-3">Visible to others as: <span className="font-mono">{maskAccount(bank.account_number)}</span></div>}
        </Section>
        <Section title="Emergency Contacts">
          <div className="space-y-2">
            {(data.emergency_contacts || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between border border-[#E5E5E5] p-3">
                <div>
                  <div className="font-medium text-sm">{c.name} <span className="text-neutral-500 text-xs">· {c.relationship}</span></div>
                  <div className="text-xs text-neutral-500">{c.phone} · {c.email}</div>
                </div>
                <button onClick={() => delEmer(c.id)} className="text-neutral-500 hover:text-red-600"><Trash size={14} /></button>
              </div>
            ))}
          </div>
          <div className="mt-3 border-t border-[#E5E5E5] pt-3 grid grid-cols-2 gap-2">
            <Input placeholder="Name" value={newEmer.name} onChange={(e) => setNewEmer({ ...newEmer, name: e.target.value })} className="rounded-none" />
            <Input placeholder="Relationship" value={newEmer.relationship} onChange={(e) => setNewEmer({ ...newEmer, relationship: e.target.value })} className="rounded-none" />
            <Input placeholder="Phone" value={newEmer.phone} onChange={(e) => setNewEmer({ ...newEmer, phone: e.target.value })} className="rounded-none" />
            <Input placeholder="Email" value={newEmer.email} onChange={(e) => setNewEmer({ ...newEmer, email: e.target.value })} className="rounded-none" />
            <Button onClick={addEmer} className="col-span-2 rounded-none bg-[#1F4E8C] text-white h-9 text-sm"><Plus size={14} className="mr-1" />Add contact</Button>
          </div>
        </Section>
        <Section title="Statutory">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="eyebrow mb-1">Tax Regime</div><div className="font-medium">{stat.tax_regime || "—"}</div></div>
            <div><div className="eyebrow mb-1">PF Applicable</div><div className="font-medium">{stat.pf_applicable ? "Yes" : "No"}</div></div>
            <div><div className="eyebrow mb-1">PT Applicable</div><div className="font-medium">{stat.pt_applicable ? "Yes" : "No"}</div></div>
            <div><div className="eyebrow mb-1">ESIC Applicable</div><div className="font-medium">{stat.esic_applicable ? "Yes" : "No"}</div></div>
          </div>
          <div className="text-xs text-neutral-500 mt-3">Statutory IDs (PAN/UAN) are managed by Payroll Admin and shown masked.</div>
        </Section>
      </div>

      <InvestmentDeclarations />
    </div>
  );
}

function InvestmentDeclarations() {
  const [decl, setDecl] = useState(null);
  const [est, setEst] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/profile/me/declarations"), api.get("/tax/estimate/me")])
      .then(([a, b]) => { setDecl(a.data); setEst(b.data); })
      .catch(() => {});
  }, []);
  const save = async () => {
    try { await api.put("/profile/me/declarations", decl); toast.success("Declaration saved · TDS will refresh next payroll"); const b = await api.get("/tax/estimate/me"); setEst(b.data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!decl) return null;
  const update = (k, v) => setDecl({ ...decl, [k]: v });
  return (
    <div className="mt-4">
      <Section title="Investment Declarations & TDS Estimate"
        right={<Button size="sm" onClick={save} className="rounded-none bg-[#FF5500] h-8 text-xs" data-testid="save-declarations">Save</Button>}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="eyebrow mb-2">Financial Year</div>
            <Input value={decl.fy_year || ""} onChange={(e) => update("fy_year", e.target.value)} className="rounded-none mb-4" data-testid="decl-fy" />
            <div className="grid grid-cols-2 gap-3">
              <div><div className="eyebrow mb-1">80C (cap ₹1.5L)</div><Input type="number" value={decl.section_80c || 0} onChange={(e) => update("section_80c", parseFloat(e.target.value) || 0)} className="rounded-none" data-testid="decl-80c" /></div>
              <div><div className="eyebrow mb-1">80D (cap ₹25k)</div><Input type="number" value={decl.section_80d || 0} onChange={(e) => update("section_80d", parseFloat(e.target.value) || 0)} className="rounded-none" /></div>
              <div><div className="eyebrow mb-1">NPS 80CCD(1B) (cap ₹50k)</div><Input type="number" value={decl.section_80ccd_1b || 0} onChange={(e) => update("section_80ccd_1b", parseFloat(e.target.value) || 0)} className="rounded-none" /></div>
              <div><div className="eyebrow mb-1">Home Loan Interest (cap ₹2L)</div><Input type="number" value={decl.home_loan_interest || 0} onChange={(e) => update("home_loan_interest", parseFloat(e.target.value) || 0)} className="rounded-none" /></div>
              <div><div className="eyebrow mb-1">Monthly Rent Paid (HRA)</div><Input type="number" value={decl.hra_actual_rent || 0} onChange={(e) => update("hra_actual_rent", parseFloat(e.target.value) || 0)} className="rounded-none" /></div>
              <div><div className="eyebrow mb-1">Metro City?</div>
                <select value={decl.hra_metro_city ? "yes" : "no"} onChange={(e) => update("hra_metro_city", e.target.value === "yes")} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white">
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
            </div>
            <div className="text-xs text-neutral-500 mt-3">Exemption caps apply only under the Old regime. The New regime offers simpler slabs with only the standard deduction.</div>
          </div>
          <div className="bg-[#F7F7F5] p-5 border border-[#E5E5E5]">
            <div className="eyebrow mb-3">Live TDS Estimate</div>
            {est?.error ? <div className="text-sm text-neutral-500">{est.error}</div> : est && (
              <>
                <div className="font-head text-5xl font-light tracking-tighter text-[#1F4E8C]">₹{(est.monthly_tds || 0).toLocaleString()}</div>
                <div className="text-xs text-neutral-500 mb-5">Monthly TDS · Regime: <span className="font-semibold uppercase">{est.regime}</span></div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Projected Annual Gross</span><span className="font-semibold">₹{(est.projected_annual_gross || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Exemptions</span><span className="font-semibold">− ₹{(est.exemptions || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between pt-2 border-t"><span>Taxable Income</span><span className="font-semibold">₹{(est.annual_taxable_income || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Projected Annual Tax (incl. 4% cess)</span><span className="font-semibold text-[#FF5500]">₹{(est.projected_annual_tax || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>YTD TDS Deducted</span><span>₹{(est.ytd_tds || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Remaining Months (FY)</span><span>{est.remaining_months_in_fy}</span></div>
                </div>
              </>
            )}
            <div className="text-[11px] text-neutral-500 mt-5 border-t pt-3">This is a projection, not a legal tax declaration. Final tax is computed at year-end basis actual investments and verified by the Payroll Admin.</div>
          </div>
        </div>
      </Section>
    </div>
  );
}
